// This must be first import
import './instrument';

// PWA test - trigger hash change v25 - test restored PWAUpdatePrompt

import '@fontsource-variable/inter/wght.css';
import 'ag-grid-enterprise';

import { ApolloProvider } from '@apollo/client';
import { ClerkProvider } from '@clerk/clerk-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LicenseManager } from 'ag-grid-enterprise';
import { Provider as JotaiProvider } from 'jotai';
import ReactDOM from 'react-dom/client';
import { RouterWrapper } from '~/RouterWrapper';
import { jotaiStore } from '~/atoms/authAtom';
import { PopoutProvider } from '~/contexts/PopoutContext';
import { ThemeManager } from '~/components/ui/theme/ThemeManager';
import { getGraphQLClient } from './lib/graphql/graphQLClient';

const PUBLISHABLE_KEY = String(process.env.REACT_CLERK_PUBLIC);

LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-070355}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Banqora}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Banqora}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Banqora}_need_to_be_licensed___{Banqora}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{28_October_2025}____[v3]_[01]_MTc2MTYwOTYwMDAwMA==ac69b75c350661839759ae8b2a7e0c2e',
);

// Handle install prompt
let deferredPrompt;

window.addEventListener('beforeinstallprompt', (e) => {
  console.log('PWA install prompt triggered');
  e.preventDefault();
  deferredPrompt = e;

  // Check if user has already declined installation
  const hasDeclinedInstall = localStorage.getItem('pwa-install-declined');

  // Don't show prompt if user has permanently declined
  if (hasDeclinedInstall === 'true') {
    console.log('User previously declined install, not showing prompt');
    return;
  }

  // Create install button instead of auto-triggering
  const installButton = document.createElement('button');
  installButton.textContent = '📱 Install App';
  installButton.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 10000;
    padding: 12px 24px;
    background: linear-gradient(45deg, #4CAF50, #45a049);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 16px;
    font-weight: bold;
    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
    animation: slideIn 0.3s ease-out;
  `;

  // Add animation
  if (!document.querySelector('#pwa-install-styles')) {
    const style = document.createElement('style');
    style.id = 'pwa-install-styles';
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
  }

  // Handle install button click
  installButton.onclick = async () => {
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const choiceResult = await deferredPrompt.userChoice;
        console.log('Install choice:', choiceResult.outcome);

        if (choiceResult.outcome === 'accepted') {
          console.log('✅ PWA installation accepted!');
          localStorage.removeItem('pwa-install-declined');
        } else {
          console.log('❌ PWA installation declined');
          localStorage.setItem('pwa-install-declined', 'true');
        }

        installButton.remove();
        deferredPrompt = null;
      } catch (error) {
        console.error('Install error:', error);
      }
    }
  };

  // Add dismiss button
  const dismissButton = document.createElement('button');
  dismissButton.textContent = '✕';
  dismissButton.style.cssText = `
    position: absolute;
    top: -8px;
    right: -8px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #f44336;
    color: white;
    border: none;
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
  `;

  dismissButton.onclick = (event) => {
    event.stopPropagation();
    localStorage.setItem('pwa-install-declined', 'true');
    installButton.remove();
    console.log('Install prompt dismissed by user');
  };

  installButton.appendChild(dismissButton);
  document.body.appendChild(installButton);

  // Auto-hide after 10 seconds if no interaction
  setTimeout(() => {
    if (document.body.contains(installButton)) {
      installButton.style.opacity = '0.7';
    }
  }, 10000);
});

const rootElement = document.getElementById('app');
if (!rootElement) throw new Error('Root element not found');

const queryClient = new QueryClient();

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      afterSignOutUrl="/"
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      allowedRedirectOrigins={['https://www.banqora.com']}
    >
      <ApolloProvider client={getGraphQLClient()}>
        <JotaiProvider store={jotaiStore}>
          <ThemeManager />
          <QueryClientProvider client={queryClient}>
            <PopoutProvider>
              <RouterWrapper />
            </PopoutProvider>
          </QueryClientProvider>
        </JotaiProvider>
      </ApolloProvider>
    </ClerkProvider>,
  );
}
